/**
 * @file order-details.tsx
 * @module app/modules/orders-delivery/order-details
 * @description Orders delivery module component for UTrade Admin.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import Notification from "../../../../notification/notification";
import { Button, Card, Col, Row } from "react-bootstrap";
import threeDot from "../../../../../_admin/assets/media/svg_uTrade/three-dot-round.svg";
import { useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";
import Loader from "../../../../../Global/loader";
import {
  FinanceManager,
  OrderCancelled,
  OrderDelivered,
  FulfillmentManager,
  OrdersDelivery,
  AddressEditPermission,
  OrderCancelPermission,
  RouteOrderLoaded,
  EditBypassPermission,
  Admin,
} from "../../../../../utils/constants";
import Method from "../../../../../utils/methods";
import { CustomSelectTable } from "../../../../custom/Select/CustomSelectTable";
import { useAuth } from "../../../auth";
import { useWarehouse } from "../../../../context/WarehouseContext";
import { useNavigationHistory } from "../../../../../utils/useNavigationHistory";
import OrderDetailStatusAlerts from "../../components/order-details/order-detail-status-alerts";
import BasicDetailsCard from "../../components/order-details/basic-details-card";
import DeliveryDetailsCard from "../../components/order-details/delivery-details-card";
import OrderSectionFallback from "../../components/order-details/order-section-fallback";
import { OrderDetails as OrderDetailsRecord } from "../../types/order-details";
import { fetchOrderDetails, updateOrderStatus, downloadOrderInvoice } from "../../services/order-detail.service";
import "../../styles/order-details.css";

// Sections below the fold and modals are split into their own chunks. The below-the-fold
// loaders are also started on mount, so they download while the order request is in flight.
const loadOrderVehicleCard = () =>
  import("../../components/order-details/order-vehicle-card");
const loadRemainingGoodsCard = () =>
  import("../../components/order-details/remaining-goods-card");
const loadOrderDeliveryTimeline = () =>
  import("../../components/order-details/order-timeline");
const loadPaymentSummaryTable = () =>
  import("../../components/order-details/payment-summary-table");
const OrderVehicleCard = lazy(loadOrderVehicleCard);
const RemainingGoodsCard = lazy(loadRemainingGoodsCard);
const OrderDeliveryTimeline = lazy(loadOrderDeliveryTimeline);
const PaymentSummaryTable = lazy(loadPaymentSummaryTable);
const CancelOrderModal = lazy(
  () => import("../../components/order-details/cancel-order-modal")
);
const InstantOrderBatchInfo = lazy(
  () => import("../../../../modals/instant-order-batch-info")
);
const PartialPaymentTransactionDetails = lazy(
  () => import("../../../../modals/partial-payment-transaction-details")
);

const hasExplicitOrdersDeliveryPermission = (
  currentUser: any,
  permission: number
): boolean => {
  const permissions = currentUser?.roleAndPermission?.permissions;
  if (!permissions?.length) return false;

  const ordersDeliveryEntry = permissions.find(
    (item: any) => Number(item.module) === Number(OrdersDelivery)
  );
  if (!ordersDeliveryEntry?.permissions?.length) return false;

  return ordersDeliveryEntry.permissions
    .map((value: any) => Number(value))
    .includes(Number(permission));
};

const OrderDetails = () => {
  const { selectedWarehouse } = useWarehouse();
  const { navigateTo } = useNavigationHistory();
  const { currentUser } = useAuth();
  const { state }: any = useLocation();
  const [fetchLoader, setFetchLoader] = useState(false);
  const [download, setDownload] = useState(false);
  const [batchInfo, setBatchInfo] = useState(false);
  const [transaction, setTransaction] = useState(false);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");
  // Mount the cancel modal on first open, then keep it mounted so its close animation still plays.
  const [cancelModalMounted, setCancelModalMounted] = useState(false);
  const [details, setDetails] = useState<OrderDetailsRecord>();

  const hasEditBypass = hasExplicitOrdersDeliveryPermission(
    currentUser,
    EditBypassPermission
  );
  // Edit product & quantities requires explicit Edit Bypass, except Super Admin.
  const canEditProducts =
    currentUser?.userType === Admin || hasEditBypass;
  const canEditAddress = Method.hasPermission(
    OrdersDelivery,
    AddressEditPermission,
    currentUser,
  );
  const canCancelOrder = Method.hasPermission(
    OrdersDelivery,
    OrderCancelPermission,
    currentUser,
  );
  // only Edit product & quantities is gated by Edit Bypass via canEditProducts.
  const isRestrictedRoleWithoutEditBypass =
    !hasEditBypass &&
    Boolean(
      currentUser?.role?.some(
        (obj: any) =>
          obj.type === FinanceManager || obj.type === FulfillmentManager,
      ),
    );
  const getCancelledByName = (order: any) => {
    const cancelLog = order?.statusesLogs?.find(
      (log: any) => log.status === OrderCancelled,
    );
    return cancelLog?.name || "";
  };
  const getOrderActionReasonText = (order: any) => {
    if (!order) {
      return "";
    }
    if (order.status === OrderCancelled) {
      const cancelledBy = getCancelledByName(order);
      const reason = order.reason?.trim();
      if (!cancelledBy && !reason) {
        return "";
      }
      if (cancelledBy && reason) {
        return `Order cancelled by ${cancelledBy}. Reason: ${reason}`;
      }
      if (cancelledBy) {
        return `Order cancelled by ${cancelledBy}.`;
      }
      return `Cancellation reason: ${reason}`;
    }
    if (order.modification?.modified) {
      const editedBy = order.modification?.name || "";
      const reason = order.reason?.trim();
      if (!editedBy && !reason) {
        return "";
      }
      if (editedBy && reason) {
        return `Order edited by ${editedBy}. Reason: ${reason}`;
      }
      if (editedBy) {
        return `Order edited by ${editedBy}.`;
      }
      return `Modification reason: ${reason}`;
    }
    return "";
  };
  const getOrderActionOptions = ({
    includeEditProducts = true,
    includeAddressEdit = true,
    includeCancel = true,
  }: {
    includeEditProducts?: boolean;
    includeAddressEdit?: boolean;
    includeCancel?: boolean;
  } = {}) => {
    const options: any[] = [];
    if (includeEditProducts && canEditProducts) {
      options.push({
        label: (
          <button
            type="button"
            className="btn btn-link order-details-action-btn order-details-action-btn--edit"
          >
            Edit product & quantities
          </button>
        ),
        value: 1,
      });
    }
    if (includeAddressEdit && canEditAddress) {
      options.push({
        label: (
          <button
            type="button"
            className="btn btn-link order-details-action-btn order-details-action-btn--edit"
          >
            Edit delivery address
          </button>
        ),
        value: 3,
      });
    }
    if (includeCancel && canCancelOrder) {
      options.push({
        label: (
          <button
            type="button"
            className="btn btn-link order-details-action-btn order-details-action-btn--cancel"
          >
            Cancel Order
          </button>
        ),
        value: 2,
      });
    }
    return options;
  };
  const canShowOrderActionsMenu = (options: { value: number }[]) =>
    options.length > 0;
  useEffect(() => {
    if (showCancelModal) {
      setCancelModalMounted(true);
    }
  }, [showCancelModal]);
  useEffect(() => {
    void loadOrderVehicleCard();
    void loadRemainingGoodsCard();
    void loadOrderDeliveryTimeline();
    void loadPaymentSummaryTable();
    void (async () => {
      setFetchLoader(true);
      await fetchDetails();
      setFetchLoader(false);
    })();
  }, []);
  const fetchDetails = async () => {
    let response = await fetchOrderDetails(
      state?.id,
      selectedWarehouse?.value || null,
    );
    if (!response?.record) {
      return;
    }
    if (response.record.variants) {
      response.record.variants = response.record.variants.filter(
        (val: any) => Number(val.stockCount) > 0 || val.isDeleted,
      );
    }
    let tempRouteVehicles: any = [];
    if (response.record.routesVehicles.length) {
      response.record.routesVehicles.map((val: any) => {
        tempRouteVehicles.push({
          ...val,
          isOpen: false,
        });
      });
      response.record.routesVehicles = tempRouteVehicles;
    }
    response.record.remainingVariantsOpen = false;
    setDetails(response.record);
  };

  const openMenuOnClick = async () => {
    setShow(true);
  };
  const onMenuClose = async () => {
    setShow(false);
  };
  const onMenuOpen = async () => {
    setShow(true);
  };
  const closeCancelModal = () => {
    if (loading) {
      return;
    }
    setShowCancelModal(false);
    setCancelReason("");
    setCancelReasonError("");
  };
  const handleCancelOrder = async () => {
    const trimmedReason = cancelReason.trim();
    if (!trimmedReason) {
      setCancelReasonError("Please enter cancellation reason.");
      return;
    }
    let params: any = {
      status: OrderCancelled,
      reason: trimmedReason,
      auditLogMessage: `Order ${details?.refKey || ""
        } cancelled. Reason: ${trimmedReason}`,
    };
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    setLoading(true);
    setFetchLoader(true);
    let response = await updateOrderStatus(
      state.id,
      params,
      selectedWarehouse?.value || null,
    );
    if (response) {

      await fetchDetails();
      closeCancelModal();
    }
    setFetchLoader(false);
    setLoading(false);
  };
  const handleOption = async (event: any, data: any) => {
    if (event.value === 1 || event.value === 3) {
      navigateTo("/orders/edit-order-details", {
        state: {
          id: data._id,
          warehouseId: data?.warehouse?.reference || "",
          addressOnly: event.value === 3,
          productsOnly: event.value === 1,
        },
      });
    } else if (event.value === 2) {
      if (!canCancelOrder) {
        return;
      }
      setShowCancelModal(true);
      setCancelReason("");
      setCancelReasonError("");
    }
  };
  const handleDownload = async () => {
    setDownload(true);
    let response = await downloadOrderInvoice(
      state.id,
      selectedWarehouse?.value || null,
    );
    if (response) {
      const pdfBlob = new Blob([response], { type: "application/pdf" });
      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(pdfBlob);
      downloadLink.download = "order_" + details?.refKey + ".pdf";
      downloadLink.click();
    }
    setDownload(false);
  };
  const handleShow = (index: number) => {
    if (!details) return;
    let temp = { ...details };
    temp.routesVehicles[index].isOpen = !temp.routesVehicles[index].isOpen;
    setDetails(temp);
  };
  return (
    <>
      {/*
    <Notification
      bgColor="bg-light-success"
      textColor="dark"
      text="You have made changes in the order details. The customer will have to pay additional TSh 255,000 at the time of delivery."
    /> */}
      {details && batchInfo ? (
        <Suspense fallback={null}>
          <InstantOrderBatchInfo
            show={batchInfo}
            onClose={() => {
              setBatchInfo(!batchInfo);
            }}
            details={details}
          />
        </Suspense>
      ) : (
        <></>
      )}
      {cancelModalMounted || showCancelModal ? (
        <Suspense fallback={null}>
          <CancelOrderModal
            show={showCancelModal}
            loading={loading}
            cancelReason={cancelReason}
            cancelReasonError={cancelReasonError}
            onClose={closeCancelModal}
            onCancel={handleCancelOrder}
            onReasonChange={(reason: string) => {
              setCancelReason(reason);
              if (cancelReasonError) {
                setCancelReasonError("");
              }
            }}
          />
        </Suspense>
      ) : (
        <></>
      )}
      {details && Object.keys(details).length ? (
        <>
          <OrderDetailStatusAlerts details={details} />
          {(() => {
            const actionReasonText = getOrderActionReasonText(details);
            return actionReasonText ? (
              <Notification
                bgColor="bg-light"
                textColor="dark"
                text={actionReasonText}
              />
            ) : (
              <></>
            );
          })()}
        </>
      ) : (
        <></>
      )}
      <div className="p-3 p-md-5 od-page">
        <Row className="g-5 g-md-8">
          {!fetchLoader ? (
            <>
              {" "}
              {details && Object.keys(details).length ? (
                <>
                  <Col md={12}>
                    <div className="od-page-header">
                      <div className="od-page-header__main">
                        <span className="od-page-header__eyebrow">
                          Order details
                        </span>
                        <h1 className="od-page-header__title">
                          {details.refKey || "Order details"}
                          {details.instantOrder ? (
                            <span className="od-chip od-chip--warning">
                              Instant Order
                            </span>
                          ) : (
                            <></>
                          )}
                        </h1>
                        {details.customer?.name ? (
                          <div className="od-page-header__meta">
                            <span>
                              Customer <strong>{details.customer.name}</strong>
                            </span>
                          </div>
                        ) : (
                          <></>
                        )}
                      </div>
                    <Row className="od-page-header__actions align-items-center g-3">
                      {transaction ? (
                        <Suspense fallback={null}>
                          <PartialPaymentTransactionDetails
                            onHide={() => {
                              setTransaction(false);
                            }}
                            transactions={details.payment.transactions}
                            totalCharge={details.payment.totalCharge}
                            show={transaction}
                          />
                        </Suspense>
                      ) : (
                        <></>
                      )}
                      {details.payment.transactions.length ? (
                        <>
                          {details.routesVehicles.some((v: any) => {
                            return v.paymentCollection.collectedPartially;
                          }) ? (
                            <Col xs="auto">
                                <Button
                                  variant="primary"
                                  className="uw-btn uw-btn-primary uw-btn-lg btn-lg"
                                  onClick={() => {
                                    setTransaction(true);
                                  }}
                                >
                                  <span className="indicator-label fs-16 fw-bold">
                                    View Transaction Details
                                  </span>
                                </Button>
                              </Col>
                          ) : (
                            <>
                              {details.instantOrder ? (
                                <>
                                  {details.payment.remainingCharge ||
                                    details.payment.transactions.length > 1 ? (
                                    <Col xs="auto">
                                        <Button
                                          variant="primary"
                                          className="uw-btn uw-btn-primary uw-btn-lg btn-lg"
                                          onClick={() => {
                                            setTransaction(true);
                                          }}
                                        >
                                          <span className="indicator-label fs-16 fw-bold">
                                            View Transaction Details
                                          </span>
                                        </Button>
                                      </Col>
                                  ) : (
                                    <></>
                                  )}
                                </>
                              ) : (
                                <></>
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <></>
                      )}
                      {details.status === OrderDelivered ? (
                        <Col xs="auto">
                          <Button
                            variant="primary"
                            className="uw-btn uw-btn-primary uw-btn-lg btn-lg"
                            onClick={() => {
                              void handleDownload();
                            }}
                          >
                            {!download && (
                              <span className="indicator-label fs-16 fw-bold">
                                Download invoice
                              </span>
                            )}
                            {download && (
                              <span
                                className="indicator-progress fs-16 fw-bold"
                                style={{ display: "block" }}
                              >
                                Please wait...
                                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                              </span>
                            )}
                          </Button>
                        </Col>
                      ) : (
                        <></>
                      )}
                      {details.instantOrder ? (
                        <Col xs="auto">
                          <Button
                            variant="primary"
                            className="uw-btn uw-btn-primary uw-btn-lg btn-lg"
                            onClick={() => {
                              setBatchInfo(true);
                            }}
                          >
                            <span className="indicator-label fs-16 fw-bold">
                              View Batch Details
                            </span>
                          </Button>
                        </Col>
                      ) : (
                        <></>
                      )}

                      {isRestrictedRoleWithoutEditBypass ? (
                        <></>
                      ) : (
                        <>
                          {details.status !== OrderCancelled &&
                            !details.routesVehicles?.some((v: any) =>
                              v.variants?.some(
                                (variant: any) =>
                                  variant.status === RouteOrderLoaded,
                              ),
                            ) ? (
                            <Col xs="auto" className="od-page-header__menu">
                              {(() => {
                                const actionOptions = getOrderActionOptions();
                                return canShowOrderActionsMenu(
                                  actionOptions,
                                ) ? (
                                  <CustomSelectTable
                                    marginLeft={"0px"}
                                    menuMargin={"-140px"}
                                    width={"220px"}
                                    placeholder={
                                      <img
                                        className="img-fluid"
                                        width={47}
                                        height={47}
                                        src={threeDot}
                                        alt=""
                                      />
                                    }
                                    options={actionOptions}
                                    backgroundColor="white"
                                    show={show}
                                    onMenuClose={() => {
                                      onMenuClose();
                                    }}
                                    openMenuOnClick={() => {
                                      openMenuOnClick();
                                    }}
                                    onMenuOpen={() => {
                                      onMenuOpen();
                                    }}
                                    onChange={(event: any) => {
                                      void handleOption(event, details);
                                    }}
                                  />
                                ) : (
                                  <></>
                                );
                              })()}
                            </Col>
                          ) : (
                            <></>
                          )}
                        </>
                      )}
                    </Row>
                    </div>
                  </Col>
                  <BasicDetailsCard details={details} getCancelledByName={getCancelledByName} />
                  <DeliveryDetailsCard details={details} />
                  {details.routesVehicles.length ? (
                    <>
                      {details.routesVehicles.map((vehicleVal: any, vehicleIndex: number) => (
                        <Suspense
                          key={`vehicle-card-${vehicleIndex}`}
                          fallback={
                            <Col xs={12}>
                              <OrderSectionFallback height={77} />
                            </Col>
                          }
                        >
                        <OrderVehicleCard
                          vehicleVal={vehicleVal}
                          vehicleIndex={vehicleIndex}
                          details={details}
                          currentUser={currentUser}
                          handleShow={handleShow}
                          navigateTo={navigateTo}
                        />
                        </Suspense>
                      ))}
                    </>
                  ) : (
                    <></>
                  )}
                  {details.hasPartialDelivery ? (
                    <Suspense
                      fallback={
                        <Col xs={12}>
                          <OrderSectionFallback height={77} />
                        </Col>
                      }
                    >
                      <RemainingGoodsCard details={details} setDetails={setDetails} />
                    </Suspense>
                  ) : (
                    <></>
                  )}{" "}
                  {details.hasPartialDelivery ? (
                    <></>
                  ) : (
                    <Col md={12}>
                      <Suspense fallback={<OrderSectionFallback height={140} />}>
                        <OrderDeliveryTimeline details={details} />
                      </Suspense>
                    </Col>
                  )}
                  <Col md={12}>
                    <Card className="od-card od-table-card">
                      <Card.Body className="p-0">
                        <Suspense fallback={<OrderSectionFallback height={240} />}>
                          <PaymentSummaryTable details={details} />
                        </Suspense>
                      </Card.Body>
                    </Card>
                  </Col>
                </>
              ) : (
                <></>
              )}
            </>
          ) : (
            <Col lg={12}>
                <div className="d-flex justify-content-center">
                  <Loader loading={fetchLoader} />
                </div>
              </Col>
          )}
        </Row>
      </div>
    </>
  );
};
export default OrderDetails;
